import React, { FC } from 'react'

import { Box, Grid, Tooltip } from '@mui/material'
import Typography from '@mui/material/Typography'
import { every } from 'lodash'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from '../../../store/hooks'
import { currencyFormatter } from '../../../utils'
import { fCurrencyShort } from '../../../utils/formatNumber'
import { EmptyData } from '../EmptyData'
import { Indicators } from '../Indicators'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'
import { PositiveVsNegativeType } from '../types'

import { CircularBarSection } from './CircularBarSection'

export const PositiveNegativeWidget: FC<PositiveVsNegativeType> = ({ moneyWise, numberWise }) => {
  const { t } = useTranslation()
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const currency = currentAccount?.currency

  const title = `"${t('positive', 'Positive')}" vs. "${t('negative', 'Negative')}"`

  const isNoData = every(
    [moneyWise?.negative, moneyWise?.positive, numberWise?.negative, numberWise?.positive],
    (value) => !value,
  )

  return (
    <StyledWidgetWrapper>
      <ReportWidgetsHeader
        text={title}
        explanation={t(
          'theGraphsRepresentTheComparisonOfPositiveAndNegativeTransactions',
          'The graphs represent the comparison of "Positive" and "Negative" transactions',
        )}
      >
        <Indicators
          positiveLabel={t('positive', 'Positive')}
          negativeLabel={t('negative', 'Negative')}
        />
      </ReportWidgetsHeader>
      {isNoData ? (
        <Box mt={4}>
          <EmptyData
            title={t('notEnoughData', 'Not enough data')}
            subtitle={t('pleaseCompleteJournalingSection')}
          />
        </Box>
      ) : (
        <Grid container spacing={2} alignItems="center" height="100%" ml={0} minWidth={0}>
          <Grid item xs={12} sm={6} minWidth={0}>
            <CircularBarSection
              key={numberWise?.positive}
              title={t('numberOfTransactions', 'Number of transactions')}
              positiveVal={numberWise?.positive}
              negativeVal={numberWise?.negative}
              positiveLabel={t('positive', 'Positive')}
              negativeLabel={t('negative', 'Negative')}
            />
          </Grid>

          <Grid item xs={12} sm={6} minWidth={0}>
            <CircularBarSection
              key={moneyWise?.positive}
              title={t('pnlByAssessment', 'P&L by assessment')}
              positiveVal={moneyWise?.positive}
              negativeVal={moneyWise?.negative}
              positiveLabel={t('positive', 'Positive')}
              negativeLabel={t('negative', 'Negative')}
              positiveTextComponent={
                <Tooltip
                  title={
                    !isNaN(moneyWise?.positive) && Number(moneyWise?.positive)
                      ? currencyFormatter(currency, moneyWise?.positive, {}, true)
                      : '-'
                  }
                  arrow
                  placement="top"
                >
                  <Typography variant="h4" fontWeight={600}>
                    {isNaN(moneyWise?.positive) || !Number(moneyWise?.positive)
                      ? '-'
                      : fCurrencyShort(moneyWise?.positive, currency)}
                  </Typography>
                </Tooltip>
              }
              negativeTextComponent={
                <Tooltip
                  title={
                    !isNaN(moneyWise?.negative) && Number(moneyWise?.negative)
                      ? currencyFormatter(currency, moneyWise?.negative, {}, true)
                      : '-'
                  }
                  arrow
                  placement="top"
                >
                  <Typography variant="h4" fontWeight={600}>
                    {isNaN(moneyWise?.negative) || !Number(moneyWise?.negative)
                      ? '-'
                      : fCurrencyShort(moneyWise?.negative, currency)}
                  </Typography>
                </Tooltip>
              }
            />
          </Grid>
        </Grid>
      )}
    </StyledWidgetWrapper>
  )
}
